import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Sparkles, ArrowRight, CheckCircle, Loader2,
  Calendar, MapPin, User as UserIcon, Mail
} from 'lucide-react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import { safeHtml } from '../lib/sanitize';
import FloatingActions from '../components/FloatingActions';
import Footer from '../components/Footer';
import RelatedTools from '../components/RelatedTools';
import PoochoGurujiComingSoon from '../components/PoochoGurujiComingSoon';
import { useLang } from '../context/LanguageContext';
import PremiumReportCards from '../components/PremiumReportCards';
import UserFriendlyReport from '../components/UserFriendlyReport';
import { trackEvent } from '../utils/analytics';
import PremiumPoochoHero from '../components/poocho/PremiumPoochoHero';
import MediaStrip from '../components/poocho/MediaStrip';
import VideoTestimonials from '../components/poocho/VideoTestimonials';
import SampleAnswerGlimpse from '../components/poocho/SampleAnswerGlimpse';
import SacredRitualReveal from '../components/poocho/SacredRitualReveal';
import PoochoLandingV2 from '../components/poocho/PoochoLandingV2';
import PostAnswerActions from '../components/poocho/PostAnswerActions';
import PricingLadder from '../components/PricingLadder';
import { detectCountryFromBrowser } from '../utils/detectCountry';
import {
  trackBeginCheckout, trackPurchase, trackPaymentFailed,
  trackConsultationCTAClick, trackWhatsAppClick,
  trackReportGenerated, trackTechnicalAnalysisExpanded, trackPremiumCTAClicked,
  trackPaymentStarted, trackPaymentSuccess,
} from '../utils/analytics';
import { trackFunnelEvent } from '../lib/poochoFunnelBeacon';

const API = process.env.REACT_APP_BACKEND_URL;
// Browser-side timezone country fallback (used when CF-IPCountry header is unavailable)
const BROWSER_COUNTRY = detectCountryFromBrowser();
const COUNTRY_HEADER = BROWSER_COUNTRY ? { 'X-Country': BROWSER_COUNTRY } : {};
const FINAL_DISCLAIMER = 'ज्योतिषीय विश्लेषण ग्रह स्थितियों एवं संभावनाओं पर आधारित वैदिक मार्गदर्शन है। अंतिम निर्णय सदैव अपने विवेक एवं योग्य वैदिक ज्योतिषी के मार्गदर्शन से ही लें।';

/**
 * Render the LLM's markdown-flavoured shastriya_analysis as proper JSX.
 *
 * Supports:
 *  - "## 1. लग्न विश्लेषण"  → styled gold H3 with top divider
 *  - "### Sub-heading"      → smaller bold heading
 *  - "**bold inline**"       → <strong>
 *  - blank lines             → paragraph break
 */
function renderShastriya(text) {
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);
  const out = [];
  let bufPara = [];
  const flushPara = () => {
    if (!bufPara.length) return;
    const joined = bufPara.join(' ');
    out.push(
      <p key={`p-${out.length}`} className="text-text-main text-[15px] leading-[1.95] mb-3"
         dangerouslySetInnerHTML={safeHtml(
           joined.replace(/\*\*(.+?)\*\*/g, '<strong class="text-gold">$1</strong>')
                 .replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<strong class="text-gold">$1</strong>')
         )} />
    );
    bufPara = [];
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); continue; }
    if (line.startsWith('## ')) {
      flushPara();
      out.push(
        <h3 key={`h2-${out.length}`}
            className="font-heading text-xl sm:text-2xl text-gold-gradient mt-6 mb-3 pb-2 border-b border-gold/25">
          {line.replace(/^##\s+/, '')}
        </h3>
      );
    } else if (line.startsWith('### ')) {
      flushPara();
      out.push(
        <h4 key={`h3-${out.length}`} className="font-heading text-base sm:text-lg text-amber-300 mt-4 mb-2">
          {line.replace(/^###\s+/, '')}
        </h4>
      );
    } else {
      bufPara.push(line);
    }
  }
  flushPara();
  return out;
}

// === LAUNCH GATE ===
// Set to true ONLY after Razorpay (or chosen payment gateway) is fully integrated and tested.
// While false, the public sees a "Coming Soon" waitlist landing — no free AI flow, no form abuse.
const POOCHO_LAUNCH_READY = true;

export default function PoochoGurujiPage() {
  const { lang } = useLang();
  const [searchParams] = useSearchParams();
  const initialSlug = searchParams.get('q');
  const initialCustom = searchParams.get('custom');

  const [step, setStep] = useState('browse'); // browse | form | processing | ritual | answer
  const [showFullPicker, setShowFullPicker] = useState(false); // Feb 2026 CRO: toggle legacy full-picker
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState('₹');
  const [currencyLabel, setCurrencyLabel] = useState('INR');
  const [isInternational, setIsInternational] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null); // {slug,question_hi,...} OR {custom_question:"..."}
  const [selectedTier, setSelectedTier] = useState('quick');
  const [form, setForm] = useState({ name: '', birth_date: '', birth_time: '', birth_place: 'Faridabad', phone: '', email: '', gender: 'male' });
  const [agreeTC, setAgreeTC] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null); // Feb 22, 2026 — structured 409/429/402 codes
  const [answer, setAnswer] = useState(null);
  // 🔒 P0 FIX (Feb 2026): the answer step's <PostAnswerActions/> and delivery
  // fallback message reference `sessionId`, but previously it only existed as
  // a local const inside submitForm — causing a `ReferenceError: sessionId is
  // not defined` runtime crash the moment we transitioned to the answer step,
  // leaving paid users on a blank/black screen. Now stored as React state so
  // it survives across the ritual → answer step transition.
  const [sessionId, setSessionId] = useState(null);
  // Feb 2026 — auto-delivery status from session-status polling. Consumed by
  // <PostAnswerActions/> so it can show a green "already sent" badge OR an
  // eye-catching "Resend on WhatsApp/Email" retry button on first failure.
  const [autoDelivery, setAutoDelivery] = useState(null);

  // ═══ Feb 22, 2026 — ₹999 Premium Pack UX state ═══
  // Fetches active pack for the current phone; renders locked-Kundli banner
  // and drives skip-payment flow when tier=premium + active pack.
  const [premiumPack, setPremiumPack] = useState(null); // {has_pack, status, questions_used, questions_remaining, expires_at, locked_kundli}
  const [premiumPackChecking, setPremiumPackChecking] = useState(false);

  const fetchPremiumPack = useCallback(async (phone) => {
    if (!phone || phone.trim().length < 8) { setPremiumPack(null); return; }
    setPremiumPackChecking(true);
    try {
      const r = await axios.get(`${API}/api/poocho-guruji/premium-pack/${encodeURIComponent(phone.trim())}`);
      setPremiumPack(r.data);
      // If pack active + locked Kundli present, auto-fill and lock the fields
      if (r.data?.has_pack && r.data?.locked_kundli?.birth_date) {
        const lk = r.data.locked_kundli;
        setForm(prev => ({
          ...prev,
          birth_date: lk.birth_date || prev.birth_date,
          birth_time: lk.birth_time || prev.birth_time,
          birth_place: lk.birth_place ? lk.birth_place.replace(/\b\w/g, c => c.toUpperCase()) : prev.birth_place,
        }));
      }
    } catch (e) {
      setPremiumPack(null);
    } finally {
      setPremiumPackChecking(false);
    }
  }, []);

  // Auto-fetch pack when phone changes (debounced) and premium tier is selected
  useEffect(() => {
    if (selectedTier !== 'premium' || !form.phone) { setPremiumPack(null); return; }
    const t = setTimeout(() => { fetchPremiumPack(form.phone); }, 600);
    return () => clearTimeout(t);
  }, [form.phone, selectedTier, fetchPremiumPack]);

  // Load data
  useEffect(() => {
    // 🎯 Feb 07, 2026 P0 — funnel beacon: page_view
    trackFunnelEvent('page_view', { meta: { referrer: document.referrer?.slice(0, 120) || 'direct' } });
    axios.get(`${API}/api/poocho-guruji/questions`).then(res => {
      setQuestions(res.data.questions || []);
      setCategories(res.data.categories || []);
    });
    axios.get(`${API}/api/poocho-guruji/tiers`, { headers: COUNTRY_HEADER }).then(res => {
      setTiers(res.data.tiers || []);
      setCurrencySymbol(res.data.currency_symbol || '₹');
      setCurrencyLabel(res.data.currency_label || res.data.currency || 'INR');
      setIsInternational(!res.data.is_india);
    });
  }, []);

  // 🪔 DEV-ONLY PREVIEW: ?__demo=1 → load mock answer to validate Premium Report UI
  // No backend call, no payment. Safe because it only triggers on explicit URL param.
  useEffect(() => {
    if (searchParams.get('__demo') !== '1') return;
    fetch('/sample_engine_output.json')
      .then(r => r.json())
      .then(engine_output => {
        setAnswer({
          headline: 'शुभ समय आ रहा है — फरवरी 2028 की दिशा में',
          // NEW (Feb 2026) user-friendly fields
          guruji_direct_answer: 'देखिए जी, आपकी शादी का योग मध्यम सहायक दृष्टि में है और सबसे अनुकूल समय फरवरी 2028 से जून 2028 के बीच आएगा। इस अवधि में ग्रहों की स्थिति विवाह के लिए विशेष अनुकूलता दर्शाती है। फिलहाल प्रतीक्षा करें और इस समय के लिए अपने जीवन को तैयार करें। धैर्य और सकारात्मक सोच रखें।',
          main_conclusion: {
            verdict_hi: '✓ मध्यम समर्थन',
            yes_or_no_hi: 'आंशिक हाँ',
            headline_finding: 'फरवरी 2028 से जून 2028 में विवाह के लिए सबसे प्रबल ग्रह योग बनेंगे।',
          },
          why_this_conclusion: [
            'विवाह के मुख्य भाव (सप्तम) पर शुभ ग्रहों का प्रभाव है।',
            'विवाह के कारक ग्रह गुरु और शुक्र इस समय विशेष अनुकूल हैं।',
            'महादशा गुरु और अन्तर्दशा शुक्र विवाह को सहयोग देती है।',
            'गोचर में लाभ-भाव पर शुभ प्रभाव बन रहे हैं।',
          ],
          when_will_happen: {
            primary_window_hi: 'फरवरी 2028 → जून 2028',
            why_this_time_hi: 'इस अवधि में महादशा गुरु और अन्तर्दशा शुक्र सप्तम भाव पर अपना सकारात्मक फल देते हैं, जो विवाह के लिए सबसे अनुकूल योग बनाते हैं।',
            alternate_windows_hi: [
              'अगले 3-6 माह: अक्टूबर 2026 → मार्च 2027 (मध्यम योग)',
              'अगले 6-12 माह: दिसंबर 2026 → जून 2027 (प्रबल योग)',
              'अगले 12-24 माह: फरवरी 2028 → जून 2028 (अति प्रबल)',
            ],
          },
          strongest_support: [
            '✅ गुरु ग्रह बलवान है और विवाह के मुख्य कारक के रूप में सहयोग कर रहा है।',
            '✅ महादशा और अन्तर्दशा दोनों विवाह के कारक ग्रहों में अनुकूल हैं।',
            '✅ गोचर में गुरु लाभ-भाव पर है — जीवनसाथी के माध्यम से शुभ फल।',
          ],
          main_obstacles: [
            '⚠️ शनि का गोचर कुछ समय विलंब का कारण बन सकता है।',
            '⚠️ केतु से थोड़ी मानसिक उलझन — निर्णय में जल्दबाजी न करें।',
          ],
          guruji_advice: 'गुरुजी की राय में आप अभी जल्दबाजी न करें। आगामी 6 माह तक अपने जीवन में सकारात्मक बदलाव लाएँ — सामाजिक एवं व्यक्तिगत जिम्मेदारियाँ पूरी करें, ध्यान और संयम बढ़ाएँ। यह समय शादी से पूर्व आत्म-तैयारी का है। चिंता और मानसिक दबाव से बचें।',
          success_probability_explained: {
            percentage: 64,
            label_hi: 'मध्यम',
            meaning_hi: 'मध्यम (64%) — सफलता संभव है, लेकिन पूर्ण रूप से सुनिश्चित नहीं। प्रयास, धैर्य और सही समय तीनों आवश्यक होंगे।',
          },
          // Legacy fields (still rendered inside collapsed "विस्तृत ज्योतिषीय विश्लेषण")
          shastriya_analysis: '## 1. प्रश्न की पहचान\nप्रश्न: विवाह — सप्तम भाव।\n\n## 2. सम्बन्धित भाव\nमुख्य भाव सप्तम — दाम्पत्य जीवन का प्रमुख निर्धारक।\n\n## 3. भावेश विश्लेषण\nसप्तमेश की स्थिति शुभ ग्रहों के प्रभाव में है।',
          shlokas: [{ sanskrit: 'सप्तमे शुभ संयोगे विवाह योगो भविष्यति॥', meaning_hi: 'सप्तम भाव में शुभ ग्रहों के योग से विवाह सिद्ध होता है।', source: 'फलदीपिका अ.9.17' }],
          decision: {
            verdict_hi: '✓ मध्यम समर्थन', verdict_en: 'Moderately Supportive', probability_pct: 64,
            favorable_period_hi: 'फरवरी 2028 → जून 2028',
            key_factors: ['गुरु महादशा 7th भावेश के रूप में', 'शुक्र अन्तर्दशा विवाह कारक', 'गोचर में गुरु लग्न पर अनुकूल'],
          },
          vyavharik_salah: 'प्रत्येक गुरुवार पीला वस्त्र धारण करें।\nॐ बृहं बृहस्पतये नमः — 108 बार दैनिक जप।',
          engine_output,
          meta: { tier: 'quick', preview_mode: true },
        });
        setSelectedQuestion({ slug: 'shadi_kab_hogi', question_hi: 'शादी कब होगी?', custom_question: null });
        setSelectedTier('quick');
        setStep('answer');
      })
      .catch(e => console.error('Demo preview load failed:', e));
  }, [searchParams]);

  // 🛡️ P0 SAFETY NET (Feb 2026 bug fix): if the Sacred Ritual overlay somehow
  // fails to fire its onComplete (e.g. framer-motion animation stall, timer
  // race), force-transition to the 'answer' step after 12s. Without this a
  // paid user could end up staring at the fixed full-screen black ritual
  // overlay forever with no scroll and no answer visible.
  useEffect(() => {
    if (step !== 'ritual') return;
    const safety = setTimeout(() => {
      setStep((cur) => (cur === 'ritual' ? 'answer' : cur));
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
    }, 12000);
    return () => clearTimeout(safety);
  }, [step]);

  // 🪔 DEV-ONLY PREVIEW: ?__ritual=1 → force render the Sacred Ritual Reveal for
  // 7-second QA preview without a live payment. Auto-transitions to /poocho-guruji.
  useEffect(() => {
    if (searchParams.get('__ritual') !== '1') return;
    setSelectedQuestion({ question_hi: 'मेरी शादी कब होगी?', custom_question: null });
    setForm((f) => ({ ...f, name: f.name || 'यजमान' }));
    // Fake minimal answer so the AnimatePresence ritual branch can render
    setAnswer({ headline: 'ritual-preview' });
    setStep('ritual');
  }, [searchParams]);

  // Preselect from URL params
  useEffect(() => {
    if (initialCustom) {
      setSelectedQuestion({ custom_question: initialCustom, question_hi: initialCustom });
      setStep('form');
    } else if (initialSlug && questions.length > 0) {
      const q = questions.find(x => x.slug === initialSlug);
      if (q) { setSelectedQuestion(q); setStep('form'); }
    }
  }, [initialSlug, initialCustom, questions]);

  // Filter
  const filtered = questions.filter(q => {
    const matchCat = activeCategory === 'all' || q.category === activeCategory;
    const matchSearch = !searchTerm.trim() || q.question_hi.toLowerCase().includes(searchTerm.toLowerCase()) || q.question_en.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCat && matchSearch;
  });

  // Feb 2026 CRO — after any question is selected, jump straight into the
  // form/payment section (not top of page). User sees selected question →
  // form fields → ₹99 submit button. First input auto-focus.
  //
  // Wait for form step to mount + framer-motion enter animation to finish
  // (~300ms) before scrolling, otherwise element is either not in DOM yet
  // or its position is mid-animation and scrollIntoView misses the target.
  const scrollToFormAndFocus = () => {
    let attempts = 0;
    const tryScroll = () => {
      const el = document.querySelector('[data-testid="pg-form-trust-strip"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => {
          const firstInput = document.querySelector('[data-testid="pg-name"] input, [data-testid="pg-name"]');
          if (firstInput && firstInput.focus) firstInput.focus({ preventScroll: true });
        }, 700);
      } else if (attempts++ < 20) {
        setTimeout(tryScroll, 100);
      }
    };
    setTimeout(tryScroll, 350);
  };

  const handleSelectQuestion = (q) => {
    setSelectedQuestion(q);
    setStep('form');
    trackFunnelEvent('question_select', { meta: { slug: q?.slug || 'unknown', category: q?.category || '' } });
    scrollToFormAndFocus();
  };

  const handleAskCustom = () => {
    if (searchTerm.trim().length < 10) return;
    setSelectedQuestion({ custom_question: searchTerm.trim(), question_hi: searchTerm.trim() });
    setStep('form');
    trackFunnelEvent('question_select', { meta: { slug: 'custom', length: searchTerm.trim().length } });
    scrollToFormAndFocus();
  };

  const submitAsk = async () => {
    setError(null);
    // 🎯 Feb 07, 2026 P0 — funnel beacon: form_submit_click
    trackFunnelEvent('form_submit_click', { meta: { tier: selectedTier, question_type: selectedQuestion?.slug ? 'catalog' : 'custom' } });
    // Field-specific validation — exact field-name in error messages
    const missing = [];
    if (!form.name) missing.push('नाम');
    if (!form.phone) missing.push('WhatsApp नंबर');
    if (!form.birth_date) missing.push('जन्म तिथि');
    if (!form.birth_time) missing.push('जन्म समय (HH:MM AM/PM तीनों चुनें)');
    if (!form.birth_place) missing.push('जन्म स्थान');
    if (missing.length) {
      setError(`कृपया ये fields भरें: ${missing.join(', ')}`);
      return;
    }
    if (!agreeTC) {
      setError('कृपया Terms स्वीकार करें');
      return;
    }
    setProcessing(true);
    try {
      const payload = {
        name: form.name, birth_date: form.birth_date, birth_time: form.birth_time,
        birth_place: form.birth_place, phone: form.phone,
        email: (form.email || '').trim() || undefined,
        gender: form.gender,
        tier: selectedTier,
      };
      if (selectedQuestion.slug) payload.question_slug = selectedQuestion.slug;
      else payload.custom_question = selectedQuestion.custom_question;

      const res = await axios.post(`${API}/api/poocho-guruji/ask`, payload, { headers: COUNTRY_HEADER });
      const sessionId = res.data.session_id;
      // Also stash in React state so it survives across the ritual → answer
      // step transition (used by <PostAnswerActions/> for delivery upsells).
      setSessionId(sessionId);

      // ═══ Feb 22, 2026 — Premium Pack: skip Razorpay, go straight to answer ═══
      if (res.data.premium_pack_active) {
        setStep('processing');
        try {
          await axios.post(`${API}/api/poocho-guruji/answer/${sessionId}`, { payment_verified: true });
          // Poll for answer (same as post-payment flow)
          let attempts = 0;
          const poll = async () => {
            attempts += 1;
            try {
              const st = await axios.get(`${API}/api/poocho-guruji/session-status/${sessionId}`);
              if (st.data.status === 'answered') {
                const ans = await axios.get(`${API}/api/poocho-guruji/answer-by-session/${sessionId}`).catch(() => null);
                if (ans?.data) setAnswer(ans.data);
                setStep('ritual');
                // Refresh pack status
                fetchPremiumPack(form.phone);
                return;
              }
              if (attempts < 30) setTimeout(poll, 4000);
              else setStep('answer');
            } catch (e) {
              if (attempts < 30) setTimeout(poll, 4000);
              else setStep('answer');
            }
          };
          poll();
        } catch (e) {
          setError('उत्तर तैयार करने में समस्या आई — कृपया कुछ देर में retry करें।');
          setStep('form');
          setProcessing(false);
        }
        return;
      }

      // === Razorpay Checkout ===
      // 1. Create order on backend
      const orderRes = await axios.post(`${API}/api/razorpay/create-order`, { session_id: sessionId });
      const { order_id, amount, currency, key_id, prefill } = orderRes.data;

      // 2. Open Razorpay popup
      if (!window.Razorpay) {
        throw new Error('Payment gateway loading... कृपया पुनः प्रयास करें');
      }

      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency,
        name: 'ViratAstro · पूछो Guruji',
        description: `${selectedQuestion.question_hi || selectedQuestion.custom_question}`.slice(0, 120),
        order_id,
        image: '/logo.png',
        prefill: {
          name: prefill?.name || form.name,
          contact: prefill?.contact || form.phone,
        },
        notes: { session_id: sessionId },
        theme: { color: '#D4AF37' },
        modal: {
          ondismiss: () => {
            setProcessing(false);
            setError('Payment cancel हो गया — कृपया फिर try करें');
            setStep('form');
          },
        },
        handler: async (rzpResp) => {
          // 3. Verify signature on backend
          setStep('processing');
          // GA4 + Google Ads purchase event — value comes from Razorpay (paise → rupees)
          trackPaymentSuccess({
            transaction_id: rzpResp.razorpay_payment_id,
            value: Math.round((amount || 9900) / 100),
            currency: currency || 'INR',
            item_name: `PoochoGuruji_${selectedTier || 'quick'}`,
            tier: selectedTier,
          });
          trackFunnelEvent('payment_success', { meta: { tier: selectedTier, amount_paise: amount } });
          try {
            await axios.post(`${API}/api/razorpay/verify-payment`, {
              session_id: sessionId,
              razorpay_order_id: rzpResp.razorpay_order_id,
              razorpay_payment_id: rzpResp.razorpay_payment_id,
              razorpay_signature: rzpResp.razorpay_signature,
            });

            // 4. Kick off async answer generation
            await axios.post(`${API}/api/poocho-guruji/answer/${sessionId}`, {
              payment_verified: true,
              payment_id: rzpResp.razorpay_payment_id,
            });

            // 5. Poll for answer (every 4s, max 30 attempts = 2 min)
            let attempts = 0;
            const poll = async () => {
              attempts += 1;
              try {
                const statusRes = await axios.get(`${API}/api/poocho-guruji/session-status/${sessionId}`);
                if (statusRes.data.status === 'answered' && statusRes.data.answer) {
                  setAnswer(statusRes.data.answer);
                  setAutoDelivery(statusRes.data.auto_delivery || null);
                  // Sacred Ritual Reveal (Phase 5 — Approval 3b): 7-sec ceremonial
                  // transition before the answer renders. Ritual component fires
                  // onComplete → setStep('answer').
                  setStep('ritual');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  return;
                }
                if (statusRes.data.status === 'generation_failed') {
                  throw new Error('generation_failed');
                }
                if (attempts >= 30) {
                  // After 2 min, fall back: tell user we'll WhatsApp it
                  setAnswer({
                    headline: 'आपका उत्तर तैयार हो रहा है 🙏',
                    intro: `धन्यवाद ${form.name} जी! आपका payment confirm हो गया है। उत्तर तैयार हो रहा है — कुछ क्षणों में आपके WhatsApp (${form.phone}) पर भेज दिया जाएगा। आप इसी पेज को कुछ देर बाद refresh भी कर सकते हैं।`,
                    shastriya_analysis: '',
                    upay: '',
                    closing: 'किसी भी प्रश्न के लिए WhatsApp +91-9311369729 पर संपर्क करें।',
                    meta: { session_id: sessionId, payment_id: rzpResp.razorpay_payment_id },
                  });
                  setStep('answer');
                  return;
                }
                setTimeout(poll, 4000);
              } catch (pollErr) {
                console.error('Poll error:', pollErr);
                if (attempts < 30) {
                  setTimeout(poll, 4000);
                } else {
                  throw pollErr;
                }
              }
            };
            setTimeout(poll, 3000);
          } catch (innerErr) {
            console.error('Post-payment error:', innerErr);
            setError(
              `भुगतान सफल है (ID: ${rzpResp.razorpay_payment_id}). उत्तर तैयार हो रहा है — कुछ मिनटों में आपके WhatsApp (${form.phone}) पर भेज दिया जाएगा। यदि न मिले तो WhatsApp +91-9311369729 पर payment ID share करें।`
            );
            setStep('form');
          } finally {
            setProcessing(false);
          }
        },
      });

      rzp.on('payment.failed', (failResp) => {
        console.error('Razorpay payment failed:', failResp);
        trackPaymentFailed({
          reason: failResp.error?.description || failResp.error?.code || 'unknown',
          transaction_id: failResp.error?.metadata?.payment_id,
          value: Math.round((amount || 9900) / 100),
        });
        trackFunnelEvent('payment_failed', { meta: { tier: selectedTier, reason: (failResp.error?.code || 'unknown').slice(0, 60) } });
        setProcessing(false);
        setError(`Payment failed: ${failResp.error?.description || 'unknown error'}`);
        setStep('form');
      });

      // Fire begin_checkout + payment_started exactly before opening Razorpay
      // popup so we can measure form-fill → checkout-open drop-off in GA4.
      trackPaymentStarted({
        value: Math.round((amount || 9900) / 100),
        currency: currency || 'INR',
        item_name: `PoochoGuruji_${selectedTier || 'quick'}`,
        tier: selectedTier,
      });
      trackFunnelEvent('payment_init', { meta: { tier: selectedTier, amount_paise: amount, currency } });
      rzp.open();
    } catch (err) {
      console.error(err);
      // ═══ Feb 22, 2026 — structured Premium Pack errors ═══
      // Backend returns 409/429/402 with { detail: {code, message, outcome} }
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code) {
        setErrorCode(detail.code);
        setError(detail.message || 'कृपया पुनः प्रयास करें।');
        // For premium tier errors, refresh pack status so user sees current state
        if (['duplicate_question','daily_quota_done','kundli_locked','expired','exhausted'].includes(detail.code)) {
          fetchPremiumPack(form.phone);
        }
      } else {
        setErrorCode(null);
        setError(typeof detail === 'string' ? detail : (err.message || 'कुछ तकनीकी समस्या आई — कृपया पुनः प्रयास करें'));
      }
      setStep('form');
      setProcessing(false);
    } finally {
      // intentionally do not setProcessing(false) here — Razorpay popup is async
      // and we set it inside the handler/ondismiss/payment.failed callbacks.
    }
  };

  // === LAUNCH GATE ===
  // While `POOCHO_LAUNCH_READY` is false, show only a Coming Soon waitlist
  // (no interactive flow, no free AI answers, no form abuse).
  if (!POOCHO_LAUNCH_READY) {
    return (
      <div className="min-h-screen bg-background" data-testid="poocho-guruji-page">
        <SEO
          title="पूछो Guruji · जल्द आ रहा है | ViratAstro"
          description="पूछो Guruji — आचार्य विवेक मुदगल जी के 33 वर्षों का अनुभव AI के साथ। मात्र ₹99 में सटीक पराशरीय उत्तर। Launch जल्द — Waitlist में register करें।"
          path="/poocho-guruji"
          image="/share-poocho-guruji.jpg"
          keywords="poocho guruji, पूछो गुरुजी, coming soon, vedic astrology AI, vivek mudgal, parashari jyotish, ₹99"
          breadcrumbs={[
            { name: 'होम', path: '/' },
            { name: 'पूछो Guruji', path: '/poocho-guruji' },
          ]}
        />
        <Header />
        <PoochoGurujiComingSoon lang={lang} />
        <FloatingActions />
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen overflow-x-hidden ${step === 'browse' ? 'bg-[#FDF1DA]' : 'bg-background'}`}
      data-testid="poocho-guruji-page"
    >
      <SEO
        title="पूछो Guruji · ₹99 वैदिक उत्तर | ViratAstro"
        description="आचार्य Vivek जी के 33 वर्षों का अनुभव — पराशरीय ज्योतिष ग्रंथों के साथ AI से तुरंत उत्तर मात्र ₹99 में। 100+ जीवन के प्रश्नों के सटीक answers।"
        path="/poocho-guruji"
        image="/brand/og/va-og-poocho-approved.png"
        keywords="poocho guruji, पूछो गुरुजी, vedic astrology AI, parashari jyotish question answer, ₹99 astrology, vivek mudgal, online jyotish consultation"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'पूछो Guruji', path: '/poocho-guruji' },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          '@id': 'https://viratastro.cloud/poocho-guruji#service',
          serviceType: 'Vedic Astrology Q&A',
          name: 'पूछो Guruji — Vedic Astrology Question Answer',
          description: 'Get an authentic Parashari-Vedic answer to any single life question — powered by Acharya Vivek Mudgal\'s 33 years of experience combined with a shastriya AI pipeline. Answer delivered on WhatsApp/email within minutes.',
          provider: {
            '@type': 'Person',
            name: 'Acharya Vivek Mudgal',
            url: 'https://viratastro.cloud/experts/vivek-virat-mudgal',
          },
          areaServed: [
            { '@type': 'Country', name: 'India' },
            { '@type': 'Country', name: 'United Arab Emirates' },
            { '@type': 'Country', name: 'United States' },
            { '@type': 'Country', name: 'United Kingdom' },
          ],
          availableChannel: {
            '@type': 'ServiceChannel',
            serviceUrl: 'https://viratastro.cloud/poocho-guruji',
            availableLanguage: ['hi', 'en'],
          },
          offers: {
            '@type': 'Offer',
            price: '99',
            priceCurrency: 'INR',
            availability: 'https://schema.org/InStock',
            url: 'https://viratastro.cloud/poocho-guruji',
            description: 'Quick single-question Parashari answer (₹99). Deep analysis (₹299) and Premium (₹999) tiers also available.',
          },
          aggregateRating: {
            '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '287', bestRating: '5',
          },
        }}
      />
      <Header />

      {/* === PREMIUM HERO (Phase 3 — Emotional Positioning) ===
          Feb 2026 CRO redesign: on browse step we render the new
          PoochoLandingV2 (which has its own hero). PremiumPoochoHero is
          kept for form/answer flows so the returning user still sees a
          consistent trust anchor above their form. */}
      {step !== 'processing' && step !== 'ritual' && step !== 'browse' && (
        <PremiumPoochoHero compact={step !== 'browse'} />
      )}

      <AnimatePresence mode="wait">
        {step === 'browse' && (
          <motion.div key="browse" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {/* ═══ Feb 2026 CRO Redesign — new consolidated landing view ═══
                Preserves handleSelectQuestion / handleAskCustom callbacks so
                the underlying question-picker → form → payment flow is 100%
                intact. Existing category/question grid + pricing ladder are
                still rendered below the new landing for users who want to
                browse all 100 pre-mapped questions. */}
            <PoochoLandingV2
              questions={questions}
              categoriesFromApi={categories}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onSelectQuestion={handleSelectQuestion}
              onShowFullPicker={() => {
                setShowFullPicker(true);
                setTimeout(() => {
                  const el = document.getElementById('poocho-question-picker');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 60);
              }}
              onAskCustom={(q) => {
                setSelectedQuestion({ custom_question: q, question_hi: q });
                setStep('form');
                trackFunnelEvent('question_select', { meta: { slug: 'custom', length: q.length } });
                scrollToFormAndFocus();
              }}
            />

            {/* Feb 2026 CRO: Legacy dark question-picker + PricingLadder are
                gated behind a toggle. Reference visual (attached by user)
                ends the browse flow at Final CTA → Footer — 9 category
                cards + hero custom input already cover discovery. */}
            {showFullPicker && (<>
            {/* === QUESTION PICKER === */}
            <section id="poocho-question-picker" className="py-16 lg:py-24 scroll-mt-24 relative bg-background">
              {/* Subtle ambient orb */}
              <div className="absolute -top-20 right-1/4 w-[400px] h-[400px] rounded-full bg-[#3d101e]/25 blur-[160px] pointer-events-none" />

              <div className="container-custom max-w-6xl relative z-10">
                {/* Section header — unified design language */}
                <div className="text-center mb-16 lg:mb-20">
                  <div className="inline-flex items-center gap-3 mb-6">
                    <span className="h-px w-8 bg-gradient-to-r from-transparent to-[#D4AF37]/60" />
                    <span className="text-[10px] uppercase tracking-[0.45em] text-[#D4AF37] font-medium">
                      100+ जीवन प्रश्न · 8 श्रेणियाँ
                    </span>
                    <span className="h-px w-8 bg-gradient-to-l from-transparent to-[#D4AF37]/60" />
                  </div>
                  <h2 className="font-heading text-[36px] sm:text-5xl lg:text-[56px] leading-[1.05] font-light text-[#F5F5F5] tracking-[-0.02em] mb-6">
                    अपना <span className="text-gold-gradient font-normal">प्रश्न</span> चुनिए
                  </h2>
                  <p className="text-[#B5B5B5] text-[15px] sm:text-[17px] font-light max-w-lg mx-auto leading-[1.85]">
                    श्रेणी से चुनें या अपने शब्दों में लिखें — Acharya Ji का उत्तर तुरंत आपके सामने।
                  </p>

                  {/* Lotus divider — unified pattern */}
                  <div className="relative flex items-center justify-center mt-12 max-w-md mx-auto">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/35 to-transparent" />
                    <div className="mx-4 relative">
                      <div className="w-2 h-2 rotate-45 bg-[#D4AF37]" />
                      <div className="absolute inset-0 w-2 h-2 rotate-45 bg-[#D4AF37] blur-sm opacity-70" />
                    </div>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/35 to-transparent" />
                  </div>
                </div>

                {/* Premium Search Bar */}
                <div className="max-w-2xl mx-auto mb-14">
                  <div className="relative group">
                    <div className="absolute -inset-px rounded-full bg-gradient-to-r from-[#D4AF37]/40 via-[#F3E5AB]/25 to-[#D4AF37]/40 blur-md opacity-60 group-focus-within:opacity-100 transition-opacity duration-500" />
                    <div className="relative flex items-center gap-3 bg-[#0A0A0A] border border-white/10 rounded-full pl-6 pr-2 py-2 shadow-[0_10px_60px_-20px_rgba(0,0,0,0.9)]">
                      <Search className="w-4 h-4 text-[#D4AF37]/80 shrink-0" strokeWidth={1.6} />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={lang === 'hi' ? 'अपना प्रश्न यहाँ लिखें...' : 'Search or type your question...'}
                        className="flex-1 bg-transparent outline-none text-[#F5F5F5] placeholder:text-[#6E6E6E] text-[15px] py-2 font-light"
                        data-testid="poocho-page-search"
                      />
                      {searchTerm.length >= 10 && !filtered.length && (
                        <button
                          onClick={handleAskCustom}
                          className="px-5 py-2.5 rounded-full bg-gradient-to-r from-[#BF953F] via-[#D4AF37] to-[#997A00] text-black font-semibold text-[13px] tracking-wide shadow-lg hover:scale-[1.02] transition-transform"
                          data-testid="poocho-ask-custom-btn"
                        >
                          पूछें →
                        </button>
                      )}
                    </div>
                  </div>
                  {searchTerm.length >= 10 && filtered.length === 0 && (
                    <p className="text-center text-[#D4AF37]/80 text-[11.5px] mt-4 tracking-wide">
                      कोई match नहीं मिला — &ldquo;पूछें&rdquo; button से custom prompt भेजें
                    </p>
                  )}
                </div>

                {/* Premium Category Cards */}
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-2.5 mb-12">
                  <CategoryChip
                    active={activeCategory === 'all'}
                    onClick={() => setActiveCategory('all')}
                    icon="✦"
                    label="सभी"
                    count={questions.length}
                    testid="cat-all"
                  />
                  {categories.map(c => (
                    <CategoryChip
                      key={c.slug}
                      active={activeCategory === c.slug}
                      onClick={() => setActiveCategory(c.slug)}
                      icon={c.icon}
                      label={c.name_hi}
                      testid={`cat-${c.slug}`}
                    />
                  ))}
                </div>

                {/* Questions Grid — refined premium */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                  {filtered.map((q, i) => (
                    <motion.button
                      key={q.slug}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.02, 0.4), duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      onClick={() => handleSelectQuestion(q)}
                      className="group relative flex items-start gap-4 px-5 py-5 rounded-2xl bg-[#0A0A0A] border border-white/[0.06] hover:border-[#D4AF37]/40 hover:bg-[#121212] hover:-translate-y-0.5 transition-[transform,border-color,background-color] duration-500 text-left"
                      data-testid={`q-${q.slug}`}
                    >
                      <span className="text-2xl shrink-0 mt-0.5 opacity-90">{q.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#F5F5F5] text-[14px] leading-snug font-normal">{q.question_hi}</p>
                        <p className="text-[10px] mt-2 uppercase tracking-[0.2em] text-[#6E6E6E]">
                          {categories.find(c => c.slug === q.category)?.name_hi}
                        </p>
                      </div>
                      <ArrowRight
                        className="w-3.5 h-3.5 text-[#D4AF37]/50 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all duration-500 shrink-0 mt-1"
                        strokeWidth={1.5}
                      />
                    </motion.button>
                  ))}
                </div>
                {filtered.length === 0 && searchTerm.length < 10 && (
                  <p className="text-center text-[#8A8A8A] py-16 font-light">कोई प्रश्न नहीं मिला</p>
                )}
              </div>
            </section>

            {/* === PRICING PLANS (₹99 / ₹299 / ₹999 / ₹5,100) — gated for reference-visual match === */}
            <PricingLadder />
            </>)}
          </motion.div>
        )}

        {/* === FORM STEP === */}
        {step === 'form' && selectedQuestion && (
          <motion.div key="form" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
            <section className="py-10">
              <div className="container-custom max-w-2xl">
                <button onClick={() => { setStep('browse'); setSelectedQuestion(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-gold text-sm mb-5 hover:underline" data-testid="pg-form-back-btn">
                  ← अन्य प्रश्न
                </button>

                {/* Feb 2026 CRO — Checkout Trust Strip (never let trust disappear at payment stage) */}
                <div className="mb-5 rounded-2xl bg-gradient-to-br from-[#1a1230]/80 to-[#0a0820]/80 border border-gold/30 p-4" data-testid="pg-form-trust-strip">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gold/30 to-amber-700/30 border border-gold/50 flex items-center justify-center text-xl shadow-lg shrink-0">
                        🕉️
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-gold/80 font-semibold">आचार्य विवेक मुदगल</div>
                        <div className="text-[11px] text-text-muted">33+ वर्ष • BPHS + फलदीपिका</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-gold">
                      <span>★★★★★</span>
                      <span className="text-text-muted">4.9 · 287+ reviews</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gold/15 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-text-muted">
                    <span>📺 Zee News · Aaj Tak · ABP · News18</span>
                    <span>⚡ WhatsApp पर ~10 min में उत्तर</span>
                    <Link to="/refund-policy" target="_blank" className="hover:text-gold underline underline-offset-2" data-testid="pg-form-refund-link">
                      Refund Policy
                    </Link>
                  </div>
                </div>

                {/* Selected Q card */}
                <div className="rounded-2xl bg-gradient-to-br from-amber-950/30 to-purple-950/30 border border-gold/40 p-5 mb-6">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gold/80 font-bold mb-1.5">आपका प्रश्न</p>
                  <p className="text-text-main text-lg font-heading leading-tight">{selectedQuestion.question_hi}</p>
                </div>

                {/* Tier picker — Feb 2026 CRO: Hidden on the ₹99 landing flow.
                    Landing sells ONLY ₹99. Upgrades (₹299 → ₹999 → ₹5,100)
                    appear as contextual cards AFTER each answer.
                    `selectedTier` stays 'quick' by default so submit → Razorpay
                    always charges ₹99 unless the user came via an upgrade
                    card (in which case `forceTier` is set from URL/state). */}
                {false && (
                <>
                <h3 className="text-xs uppercase tracking-[0.25em] text-gold/80 font-bold mb-3">अपना plan चुनें</h3>
                <div className="grid sm:grid-cols-3 gap-3 mb-7">
                  {tiers.filter(t => t.id !== 'acharya').map(t => (
                    <button
                      key={t.id}
                      onClick={() => { setSelectedTier(t.id); trackFunnelEvent('tier_select', { meta: { tier: t.id, price: t.price } }); }}
                      className={`relative p-4 rounded-2xl border-2 text-left transition-all ${selectedTier === t.id ? 'border-gold bg-gold/10 shadow-lg shadow-gold/20' : 'border-gold/20 bg-white/[0.03] hover:border-gold/40'}`}
                      data-testid={`tier-${t.id}`}
                    >
                      {t.popular && <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-gold text-purple-950 text-[9px] font-bold uppercase tracking-wider">Popular</span>}
                      <div className="font-heading text-lg text-gold">{t.name_hi}</div>
                      <div className="text-2xl font-bold text-text-main">{currencySymbol}{t.price}</div>
                      <div className="text-[11px] text-text-muted mt-0.5">{t.questions} प्रश्न • {t.validity}</div>
                    </button>
                  ))}
                </div>
                </>
                )}

                {/* Feb 2026 CRO — clear ₹99 confirmation strip in place of the picker */}
                <div className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-gold/40 bg-gold/10 px-4 py-3" data-testid="pg-form-tier-locked">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-gold/80 font-bold">आपका plan</div>
                    <div className="text-text-main font-heading text-lg leading-tight">पूछो गुरुजी · Quick — <span className="text-gold">₹99</span></div>
                    <div className="text-[11px] text-text-muted mt-0.5">1 प्रश्न • WhatsApp पर ~10 min में उत्तर</div>
                  </div>
                  <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center shadow" style={{ background: 'linear-gradient(135deg,#F4D477,#B8942D)' }}>
                    <CheckCircle className="w-5 h-5 text-[#2c0b14]" />
                  </div>
                </div>
                {isInternational && (
                  <p className="text-[11px] text-amber-300/80 italic -mt-4 mb-4 text-center">
                    🌍 International pricing detected — payment will be processed in {currencyLabel} via Razorpay
                  </p>
                )}

                {/* ═══ Feb 22, 2026 — Premium Pack Active Banner ═══ */}
                {selectedTier === 'premium' && premiumPack?.has_pack && premiumPack.status === 'active' && (
                  <PremiumPackBanner pack={premiumPack} data-testid="premium-pack-banner" />
                )}
                {selectedTier === 'premium' && premiumPackChecking && (
                  <div className="mb-5 p-3 rounded-xl bg-white/[0.03] border border-gold/15 text-text-muted text-xs" data-testid="premium-pack-checking">
                    <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-2 text-gold/60" />
                    आपका ₹999 पैक status check हो रहा है…
                  </div>
                )}

                {/* Birth form */}
                <h3 className="text-xs uppercase tracking-[0.25em] text-gold/80 font-bold mb-3">जन्म विवरण</h3>
                {/* Birth form — locked (readOnly) when Premium Pack is active for this phone */}
                {(() => {
                  const kundliLocked = selectedTier === 'premium' && premiumPack?.has_pack && premiumPack.status === 'active';
                  return (
                <div className="grid sm:grid-cols-2 gap-3 mb-5" data-testid={kundliLocked ? "kundli-locked-fields" : "kundli-editable-fields"}>
                  <FormField icon={UserIcon} label="नाम" value={form.name} onChange={(v) => setForm({...form, name: v})} testid="pg-name" fieldName="name" />
                  <FormField icon={Sparkles} label="WhatsApp Number" value={form.phone} onChange={(v) => setForm({...form, phone: v})} testid="pg-phone" type="tel" fieldName="phone" />
                  <FormField icon={Mail} label="Email (PDF/उत्तर के लिए — optional)" value={form.email} onChange={(v) => setForm({...form, email: v})} testid="pg-email" type="email" full fieldName="email" />
                  <FormField icon={Calendar} label={kundliLocked ? "जन्म तिथि 🔒" : "जन्म तिथि"} value={form.birth_date} onChange={(v) => !kundliLocked && setForm({...form, birth_date: v})} testid="pg-dob" type="date" fieldName="birth_date" readOnly={kundliLocked} />
                  <TimeField12 value={form.birth_time} onChange={(v) => !kundliLocked && setForm({...form, birth_time: v})} readOnly={kundliLocked} />
                  <FormField icon={MapPin} label={kundliLocked ? "जन्म स्थान 🔒" : "जन्म स्थान"} value={form.birth_place} onChange={(v) => !kundliLocked && setForm({...form, birth_place: v})} testid="pg-place" full fieldName="birth_place" readOnly={kundliLocked} />
                </div>
                  );
                })()}

                {/* T&C — grid layout (predictable column widths, immune to flex auto-sizing bugs) */}
                <div className="grid grid-cols-[auto_1fr] gap-3 mb-5 items-start max-w-full">
                  <input
                    id="pg-tc-checkbox"
                    type="checkbox"
                    checked={agreeTC}
                    onChange={(e) => { setAgreeTC(e.target.checked); trackFunnelEvent(e.target.checked ? 'form_focus' : 'form_blur', { field: 'agree_tc' }); }}
                    onFocus={() => trackFunnelEvent('form_focus', { field: 'agree_tc' })}
                    className="mt-1"
                    data-testid="pg-tc-checkbox"
                  />
                  <label htmlFor="pg-tc-checkbox" className="text-text-muted text-xs leading-relaxed cursor-pointer break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 }}>
                    मैं स्वीकार करता/करती हूँ कि <strong className="text-gold">यह वैदिक विश्लेषण सेवा स्वचालित डिजिटल मार्गदर्शन पर आधारित है। विश्लेषण प्रदान होने के पश्चात सेवा पूर्ण मानी जाएगी।</strong> यह आध्यात्मिक/शास्त्रीय मार्गदर्शन हेतु है — चिकित्सीय, कानूनी, वित्तीय सलाह नहीं। मैं हमारी <Link to="/privacy-policy" target="_blank" className="text-gold underline">Privacy Policy</Link>, <Link to="/terms" target="_blank" className="text-gold underline">Terms</Link> एवं <Link to="/refund-policy" target="_blank" className="text-gold underline" data-testid="pg-refund-policy-link">Refund Policy</Link> स्वीकार करता/करती हूँ।
                  </label>
                </div>

                {error && (
                  <div
                    className={`mb-4 p-3.5 rounded-xl text-sm border ${
                      errorCode === 'kundli_locked' ? 'bg-amber-500/10 border-amber-500/40 text-amber-200'
                      : errorCode === 'duplicate_question' ? 'bg-blue-500/10 border-blue-500/40 text-blue-200'
                      : errorCode === 'daily_quota_done' ? 'bg-purple-500/10 border-purple-500/40 text-purple-200'
                      : (errorCode === 'expired' || errorCode === 'exhausted') ? 'bg-orange-500/10 border-orange-500/40 text-orange-200'
                      : 'bg-red-500/10 border-red-500/30 text-red-300'
                    }`}
                    data-testid={errorCode ? `pg-error-${errorCode}` : 'pg-error'}
                  >
                    <div className="flex items-start gap-2.5">
                      <span className="text-lg leading-none mt-0.5">
                        {errorCode === 'kundli_locked' ? '🔒'
                          : errorCode === 'duplicate_question' ? '↻'
                          : errorCode === 'daily_quota_done' ? '🌙'
                          : (errorCode === 'expired' || errorCode === 'exhausted') ? '⏳'
                          : '⚠️'}
                      </span>
                      <div>{error}</div>
                    </div>
                  </div>
                )}

                <button
                  onClick={submitAsk}
                  disabled={processing || !agreeTC}
                  className="w-full px-6 py-4 rounded-full bg-gradient-to-r from-gold via-amber-400 to-amber-500 text-purple-950 font-bold text-lg shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="pg-submit-btn"
                >
                  {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                  {selectedTier === 'premium' && premiumPack?.has_pack && premiumPack.status === 'active'
                    ? 'आज का प्रश्न पूछें (पहले से active pack)'
                    : `${currencySymbol}${tiers.find(t => t.id === selectedTier)?.price || 99} में उत्तर पाएँ`}
                </button>
                <p className="text-center text-[10px] text-text-muted/60 mt-3">
                  🔒 100% सुरक्षित — Razorpay payment gateway (UPI / Card / NetBanking)
                </p>
              </div>
            </section>
          </motion.div>
        )}

        {/* === PROCESSING === */}
        {step === 'processing' && (
          <motion.div key="proc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-20 relative overflow-hidden">
            {/* Subtle rotating yantra backdrop */}
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] pointer-events-none opacity-25"
              animate={{ rotate: 360 }}
              transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
            >
              <svg viewBox="0 0 400 400" className="w-full h-full">
                {[190, 160, 128, 96].map((r) => (
                  <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="#D4AF37" strokeOpacity="0.35" strokeWidth="0.6" />
                ))}
                {Array.from({ length: 12 }).map((_, i) => (
                  <line key={i} x1="200" y1="200" x2="200" y2="12" stroke="#D4AF37" strokeOpacity="0.28" strokeWidth="0.5"
                    transform={`rotate(${(i * 360) / 12} 200 200)`} />
                ))}
              </svg>
            </motion.div>
            <div className="container-custom max-w-md text-center relative z-10">
              <div className="inline-flex flex-col items-center gap-4 mb-6">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
                  <motion.div
                    className="absolute inset-3 rounded-full bg-gold/20 blur-md"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.8, 0.4] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                  />
                </div>
                <span className="text-[10px] uppercase tracking-[0.35em] text-gold/70 font-semibold">
                  पराशरीय विश्लेषण
                </span>
              </div>
              <h3 className="font-heading text-2xl text-text-main mb-3">आपकी कुंडली का विश्लेषण हो रहा है...</h3>
              <p className="text-text-muted text-sm leading-relaxed">
                आचार्य Vivek जी पराशर शास्त्र के माध्यम से आपके प्रश्न पर गहन विचार कर रहे हैं। यह 60-90 सेकंड लग सकता है।
              </p>
            </div>
          </motion.div>
        )}

        {/* === SACRED RITUAL REVEAL (Phase 5 — Approval 3b) === */}
        {step === 'ritual' && answer && (
          <motion.div key="ritual" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <SacredRitualReveal
              userName={form.name}
              question={selectedQuestion?.question_hi || selectedQuestion?.custom_question || ''}
              onComplete={() => {
                setStep('answer');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </motion.div>
        )}

        {/* === ANSWER === */}
        {step === 'answer' && answer && (
          <motion.div key="ans" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <AnswerDisplay answer={answer} question={selectedQuestion?.question_hi} tier={selectedTier} />
            <PostAnswerActions
              sessionId={sessionId}
              defaultPhone={form.phone}
              defaultEmail={form.email}
              currentTier={selectedTier}
              autoDelivery={autoDelivery}
              onUpgrade={(nextTier) => {
                trackEvent('poocho_upgrade_click', { from: selectedTier, to: nextTier });
                setSelectedTier(nextTier);
                setSelectedQuestion(null);
                setAnswer(null);
                setStep('browse');
                setTimeout(() => {
                  const el = document.getElementById('poocho-question-picker') || document.querySelector('[data-testid="pv2-hero-input"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 300);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Feb 2026 CRO — RelatedTools hidden during entire browse/form/etc.
          Reference visual (attached by user) ends the browse flow at Final
          CTA band → Footer. Other astrology tools are already reachable
          from the "ज्योतिषीय उपकरण" nav dropdown. */}
      <FloatingActions />
      <Footer />
    </div>
  );
}

// ============ SUB-COMPONENTS ============

const JourneyStep = ({ status, step, badge, title, subtitle, features, ctaHref, ctaLabel, onClick, testid, last = false }) => {
  const isCurrent = status === 'current';
  const isPremium = status === 'premium';
  return (
    <div className={`relative pl-0 sm:pl-20 ${last ? '' : 'pb-6'}`} data-testid={testid || `journey-${status}`}>
      {/* Step number node */}
      <div className="hidden sm:flex absolute left-0 top-6 w-12 h-12 rounded-full items-center justify-center border font-heading font-light text-sm tracking-wider z-10 bg-[#050505]"
        style={{
          borderColor: isCurrent ? 'rgba(212,175,55,0.6)' : isPremium ? 'rgba(212,175,55,0.5)' : 'rgba(212,175,55,0.35)',
          color: isCurrent ? '#D4AF37' : '#F5F5F5',
        }}>
        {step}
      </div>
      {/* Card */}
      <div className={`relative rounded-[28px] p-6 sm:p-8 transition-[border-color] duration-500 ${
        isCurrent
          ? 'bg-[#0A0A0A] border border-[#D4AF37]/25'
          : isPremium
            ? 'bg-gradient-to-br from-[#1a0b30] via-[#0A0A0A] to-[#0A0A0A] border border-[#D4AF37]/45 shadow-[0_0_40px_-14px_rgba(212,175,55,0.35)]'
            : 'bg-[#0A0A0A] border border-white/[0.08] hover:border-[#D4AF37]/25'
      }`}>
        {/* Badge */}
        <div className="absolute -top-3 left-6 sm:left-8">
          <span className={`px-3 py-1 rounded-full text-[9.5px] font-semibold tracking-[0.25em] uppercase ${
            isCurrent
              ? 'bg-[#D4AF37] text-black'
              : isPremium
                ? 'bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#997A00] text-black'
                : 'bg-[#0A0A0A] border border-[#D4AF37]/40 text-[#D4AF37]'
          }`}>
            {badge}
          </span>
        </div>

        <div className="flex items-baseline justify-between gap-4 mb-5 mt-1">
          <div>
            <h4 className="font-heading text-xl sm:text-2xl text-[#F5F5F5] font-light tracking-tight leading-snug">
              {title}
            </h4>
            <p className="text-[#A3A3A3] text-[13px] mt-1.5 font-light">{subtitle}</p>
          </div>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px] text-[#D0D0D0] font-light mb-6">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5 leading-relaxed">
              <span className={`mt-1.5 shrink-0 ${isCurrent ? 'text-[#D4AF37]' : 'text-[#D4AF37]/70'}`}>
                {isCurrent ? '✓' : '◇'}
              </span>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {ctaHref && (
          <Link
            to={ctaHref}
            onClick={onClick}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] font-semibold tracking-wide transition-[transform,box-shadow] duration-500 hover:scale-[1.02] ${
              isPremium
                ? 'bg-gradient-to-r from-[#BF953F] via-[#D4AF37] to-[#997A00] text-black shadow-[0_10px_40px_-10px_rgba(212,175,55,0.5)] hover:shadow-[0_10px_60px_-6px_rgba(212,175,55,0.7)]'
                : 'border border-[#D4AF37]/45 text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37]/[0.06]'
            }`}
          >
            {ctaLabel}
            <span aria-hidden>→</span>
          </Link>
        )}

        {isCurrent && (
          <div className="text-[11px] text-[#8A8A8A] tracking-wide italic font-light">
            ✓ यह आपने पहले ही प्राप्त कर लिया है
          </div>
        )}
      </div>
    </div>
  );
};

const CategoryChip = ({ active, onClick, icon, label, count, testid }) => (
  <button
    onClick={onClick}
    className={`group relative flex flex-col items-center gap-3 px-3 py-5 rounded-2xl transition-[transform,background-color,border-color,box-shadow] duration-700 hover:-translate-y-1 ${
      active
        ? 'bg-gradient-to-b from-[#1a0b30] to-[#0A0A0A] border border-[#D4AF37]/55 shadow-[0_20px_50px_-20px_rgba(212,175,55,0.4)]'
        : 'bg-white/[0.025] border border-white/[0.06] hover:border-[#D4AF37]/25 hover:bg-white/[0.04]'
    }`}
    data-testid={testid}
  >
    <div
      className={`w-9 h-9 rounded-full flex items-center justify-center text-lg leading-none transition-all duration-500 ${
        active
          ? 'bg-[#D4AF37]/18 border border-[#D4AF37]/40 text-[#D4AF37]'
          : 'bg-white/[0.04] border border-white/[0.05] text-[#B5B5B5] group-hover:border-[#D4AF37]/25'
      }`}
    >
      {icon}
    </div>
    <span
      className={`text-[11px] leading-tight tracking-[0.06em] font-light ${
        active ? 'text-[#F5F5F5]' : 'text-[#A3A3A3] group-hover:text-[#F5F5F5]'
      } transition-colors duration-500`}
    >
      {label}
      {typeof count === 'number' && (
        <span className="text-[9px] ml-1 opacity-60">({count})</span>
      )}
    </span>
  </button>
);

const FormField = ({ icon: Icon, label, value, onChange, testid, type = 'text', full, fieldName, readOnly = false }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className="block text-xs text-text-muted mb-1.5 flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-gold" /> {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => !readOnly && onChange(e.target.value)}
      onFocus={() => fieldName && !readOnly && trackFunnelEvent('form_focus', { field: fieldName })}
      onBlur={(e) => fieldName && !readOnly && trackFunnelEvent('form_blur', { field: fieldName, meta: { filled: Boolean(e.target.value?.trim()) } })}
      readOnly={readOnly}
      className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none transition-colors ${
        readOnly
          ? 'bg-white/[0.02] border-gold/40 text-gold/90 cursor-not-allowed'
          : 'bg-white/[0.04] border-gold/20 text-text-main focus:border-gold'
      }`}
      data-testid={testid}
    />
  </div>
);

/**
 * Birth-time picker with 3 separate dropdowns: HH (1-12), MM (00-59), AM/PM.
 *
 * Why this exists: Chrome's native <input type="time"> requires the user
 * to fill AM/PM separately. Many users (especially on macOS) miss this and
 * the form looks "filled" but value stays empty → "कृपया सभी fields भरें" bug.
 *
 * Output: always emits a clean 24-hour "HH:MM" string only when all 3 segments
 * are valid — same wire-format the backend already expects.
 */
const TimeField12 = ({ value, onChange, readOnly = false }) => {
  // Parse incoming 24-hour value into hour12 / minute / ampm
  const parseValue = (v) => {
    if (!v || !/^\d{1,2}:\d{2}$/.test(v)) return { hh: '', mm: '', ap: '' };
    const [h, m] = v.split(':').map(Number);
    const ap = h >= 12 ? 'PM' : 'AM';
    const hh = h === 0 ? 12 : (h > 12 ? h - 12 : h);
    return { hh: String(hh), mm: String(m).padStart(2, '0'), ap };
  };
  const [parts, setParts] = React.useState(parseValue(value));
  React.useEffect(() => { setParts(parseValue(value)); }, [value]);

  const emit = (p) => {
    if (p.hh && p.mm && p.ap) {
      let h = parseInt(p.hh, 10);
      if (p.ap === 'PM' && h !== 12) h += 12;
      if (p.ap === 'AM' && h === 12) h = 0;
      onChange(`${String(h).padStart(2, '0')}:${p.mm}`);
    } else {
      onChange('');
    }
  };

  const update = (key) => (e) => {
    const next = { ...parts, [key]: e.target.value };
    setParts(next);
    emit(next);
  };

  const sel = "px-3 py-2.5 rounded-xl bg-white/[0.04] border border-gold/20 text-text-main text-sm focus:border-gold focus:outline-none transition-colors appearance-none";
  const selLocked = "px-3 py-2.5 rounded-xl bg-white/[0.02] border border-gold/40 text-gold/90 text-sm cursor-not-allowed appearance-none";
  return (
    <div>
      <label className="block text-xs text-text-muted mb-1.5 flex items-center gap-1.5">
        <Calendar className="w-3.5 h-3.5 text-gold" /> जन्म समय (घंटा : मिनट : AM/PM){readOnly ? ' 🔒' : ''}
      </label>
      <div className="grid grid-cols-3 gap-2" onFocus={() => !readOnly && trackFunnelEvent('form_focus', { field: 'birth_time' })} onBlur={(e) => !readOnly && trackFunnelEvent('form_blur', { field: 'birth_time', meta: { filled: Boolean(parts.hh && parts.mm && parts.ap) } })}>
        <select className={readOnly ? selLocked : sel} value={parts.hh} onChange={update('hh')} disabled={readOnly} data-testid="pg-time-hh">
          <option value="">घंटा</option>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>
        <select className={readOnly ? selLocked : sel} value={parts.mm} onChange={update('mm')} disabled={readOnly} data-testid="pg-time-mm">
          <option value="">मिनट</option>
          {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <select className={readOnly ? selLocked : sel} value={parts.ap} onChange={update('ap')} disabled={readOnly} data-testid="pg-time-ap">
          <option value="">AM/PM</option>
          <option value="AM">AM (सुबह)</option>
          <option value="PM">PM (दोपहर/रात)</option>
        </select>
      </div>
      {(!parts.hh || !parts.mm || !parts.ap) && (
        <p className="text-[11px] text-amber-400/80 mt-1.5">तीनों field भरना ज़रूरी है — घंटा, मिनट और AM/PM</p>
      )}
    </div>
  );
};

const AnswerDisplay = ({ answer, question, tier }) => {
  // Detect new conclusion-first format (Feb 2026 Guruji mandate)
  const hasUserFriendly = !!(answer?.guruji_direct_answer || answer?.main_conclusion || answer?.why_this_conclusion);

  // Fire GA4 `report_generated` once when the answer is rendered for the first time
  React.useEffect(() => {
    if (!answer) return;
    trackReportGenerated({
      session_id: answer?.session_id,
      tier: tier || answer?.meta?.tier || 'quick',
      value: tier === 'premium' ? 999 : tier === 'deep' ? 299 : 99,
      verdict: answer?.main_conclusion?.verdict_hi || answer?.decision?.verdict_hi,
      probability_pct: answer?.success_probability_explained?.percentage || answer?.decision?.probability_pct,
    });
  }, []);

  return (
  <section className="py-10">
    <div className="container-custom max-w-3xl space-y-6">
      {/* Question */}
      <div className="rounded-2xl bg-gradient-to-br from-amber-950/30 to-purple-950/30 border border-gold/40 p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-gold/80 font-bold mb-1.5">📿 आपका प्रश्न</p>
        <p className="text-text-main text-lg font-heading">{question}</p>
      </div>

      {/* Headline */}
      <motion.h2
        initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="font-heading text-3xl sm:text-4xl text-gold-gradient leading-tight text-center px-2"
        data-testid="ans-headline"
      >
        &ldquo;{answer.headline}&rdquo;
      </motion.h2>

      {/* NEW (Feb 2026): User-friendly conclusion-first report — 8 cards */}
      {hasUserFriendly ? (
        <UserFriendlyReport answer={answer}>
          {/* Inside collapsed "विस्तृत ज्योतिषीय विश्लेषण" — old technical sections */}
          {answer.shastriya_analysis && (
            <div className="rounded-2xl bg-white/[0.025] border border-gold/15 p-4 sm:p-5">
              <h3 className="text-xs uppercase tracking-[0.25em] text-gold/80 font-bold mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> शास्त्रीय विवेचना (14 sections)
              </h3>
              <div className="text-text-main text-[14px]" data-testid="ans-shastriya">
                {renderShastriya(answer.shastriya_analysis)}
              </div>
            </div>
          )}
          {(answer.shlokas || []).length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs uppercase tracking-[0.25em] text-gold/80 font-bold flex items-center gap-2">
                🕉️ पराशरीय श्लोक
              </h3>
              {answer.shlokas.map((s, i) => (
                <div key={i} className="rounded-2xl bg-gradient-to-br from-amber-900/20 to-orange-900/10 border-l-4 border-gold p-4">
                  <p className="text-gold font-heading text-base sm:text-lg leading-[1.9] italic whitespace-pre-line mb-2">{s.sanskrit}</p>
                  <p className="text-text-muted text-sm mb-1.5 border-t border-gold/15 pt-2">— {s.meaning_hi}</p>
                  <p className="text-[11px] text-gold/70">📿 {s.source}</p>
                </div>
              ))}
            </div>
          )}
          {answer.engine_output && (
            <div data-testid="premium-report-section">
              <PremiumReportCards
                engine_output={answer.engine_output}
                decision={answer.decision}
                vyavharik_salah={answer.vyavharik_salah}
              />
            </div>
          )}
        </UserFriendlyReport>
      ) : (
        <>
          {/* Legacy layout fallback — for sessions answered before Feb 2026 user-friendly format */}
          {answer.intro && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="rounded-2xl bg-white/[0.03] border border-gold/15 p-5">
              <p className="text-text-main text-base leading-[1.85] whitespace-pre-line" data-testid="ans-intro">{answer.intro}</p>
            </motion.div>
          )}
          {answer.shastriya_analysis && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="rounded-2xl bg-white/[0.03] border border-gold/15 p-5 sm:p-6">
              <h3 className="text-xs uppercase tracking-[0.25em] text-gold/80 font-bold mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> शास्त्रीय विवेचना
              </h3>
              <div className="text-text-main" data-testid="ans-shastriya">
                {renderShastriya(answer.shastriya_analysis)}
              </div>
            </motion.div>
          )}
          {(answer.shlokas || []).length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="space-y-3">
              <h3 className="text-xs uppercase tracking-[0.25em] text-gold/80 font-bold flex items-center gap-2">
                🕉️ पराशरीय श्लोक
              </h3>
              {answer.shlokas.map((s, i) => (
                <div key={i} className="rounded-2xl bg-gradient-to-br from-amber-900/20 to-orange-900/10 border-l-4 border-gold p-4">
                  <p className="text-gold font-heading text-base sm:text-lg leading-[1.9] italic whitespace-pre-line mb-2">{s.sanskrit}</p>
                  <p className="text-text-muted text-sm mb-1.5 border-t border-gold/15 pt-2">— {s.meaning_hi}</p>
                  <p className="text-[11px] text-gold/70">📿 {s.source}</p>
                </div>
              ))}
            </motion.div>
          )}
          {answer.engine_output && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
              data-testid="premium-report-section">
              <PremiumReportCards
                engine_output={answer.engine_output}
                decision={answer.decision}
                vyavharik_salah={answer.vyavharik_salah}
              />
            </motion.div>
          )}
        </>
      )}

      {/* Final disclaimer */}
      <div className="rounded-xl bg-amber-500/5 border border-amber-500/25 p-4 text-center">
        <p className="text-xs text-amber-300 italic leading-relaxed">
          ⚠️ ज्योतिषीय विश्लेषण ग्रह स्थितियों एवं संभावनाओं पर आधारित वैदिक मार्गदर्शन है।
          अंतिम निर्णय सदैव अपने विवेक एवं योग्य वैदिक ज्योतिषी के मार्गदर्शन से ही लें।
        </p>
      </div>

      {/* Upgrade Information Box — only for the ₹99 (quick) tier */}
      {tier === 'quick' && (
        <div className="relative mt-6" data-testid="ans-upgrade-info">
          {/* Section header */}
          <div className="text-center mb-10">
            <p className="text-[10.5px] uppercase tracking-[0.35em] text-[#D4AF37] mb-4 font-medium">
              अगला चरण · Next Step
            </p>
            <h3 className="font-heading text-[26px] sm:text-[32px] lg:text-[38px] text-[#F5F5F5] font-light leading-tight tracking-tight mb-3">
              और <span className="text-gold-gradient font-normal">गहराई</span> से जानें?
            </h3>
            <p className="text-[#A3A3A3] text-sm font-light max-w-md mx-auto leading-relaxed">
              आपकी ₹99 रिपोर्ट पूर्ण है। अग्रिम स्तर केवल गहरे विश्लेषण के लिए हैं।
            </p>
            <div className="relative flex items-center justify-center mt-8 max-w-[220px] mx-auto">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/25 to-transparent" />
              <div className="mx-3 w-1.5 h-1.5 rotate-45 bg-[#D4AF37]/60" />
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/25 to-transparent" />
            </div>
          </div>

          {/* Vertical journey with connecting rail */}
          <div className="relative max-w-3xl mx-auto">
            {/* Connecting rail */}
            <div className="absolute left-6 sm:left-8 top-8 bottom-8 w-px bg-gradient-to-b from-[#D4AF37]/20 via-[#D4AF37]/10 to-[#D4AF37]/20 hidden sm:block" />

            {/* Current tier — ₹99 (delivered) */}
            <JourneyStep
              status="current"
              step="01"
              badge="अभी प्राप्त"
              title="₹99 · तत्काल उत्तर"
              subtitle="आपकी वर्तमान रिपोर्ट में शामिल"
              features={[
                'भाव, भावेश एवं कारक ग्रह विश्लेषण',
                'नक्षत्र, पाद, दशा एवं गोचर विश्लेषण',
                'सफलता संभावना (%) + Timing Window',
                'व्यावहारिक उपाय',
              ]}
            />

            {/* Deep 299 */}
            <JourneyStep
              status="next"
              step="02"
              badge="अनुशंसित"
              title="₹299 · Deep Analysis"
              subtitle="ग्रह-बल, अवरोध एवं विस्तृत टाइमिंग"
              features={[
                'ग्रह बल (Shad-Bala) विश्लेषण',
                'शुभ-अशुभ ग्रह इन्टरैक्शन',
                'योग एवं अवरोधों का अध्ययन',
                'विस्तृत Timing Windows',
                'उन्नत उपाय एवं ratna सुझाव',
              ]}
              ctaHref="/poocho-guruji?tier=deep"
              ctaLabel="Deep Analysis अनलॉक करें"
              onClick={() => trackPremiumCTAClicked({ source: 'answer_upgrade_box', target_tier: 'deep', current_tier: tier || 'quick' })}
              testid="ans-deep-info-cta"
            />

            {/* Premium 999 — Poocho Guruji 30-day AI guidance */}
            <JourneyStep
              status="premium"
              step="03"
              badge="30-दिन दैनिक मार्गदर्शन"
              title="₹999 · पूछो गुरु जी"
              subtitle="Personalised Kundli-based AI Guidance"
              features={[
                '30 दिनों तक प्रतिदिन 1 व्यक्तिगत ज्योतिषीय मार्गदर्शन',
                'वैदिक पाराशरीय ज्योतिष के शास्त्रीय सिद्धांतों के अनुसार',
                'Dasha, Gochar एवं Kundli आधारित उत्तर',
                'Kundli, प्रश्न एवं उत्तर History स्वतः सुरक्षित',
              ]}
              ctaHref="/poocho-guruji?tier=premium"
              ctaLabel="₹999 Poocho Guruji अनलॉक करें"
              onClick={() => trackPremiumCTAClicked({ source: 'answer_upgrade_box', target_tier: 'premium', current_tier: tier || 'quick' })}
              testid="ans-premium-info-cta"
              last
            />
          </div>

          <p className="text-[11px] text-[#6E6E6E] text-center mt-10 tracking-wide font-light max-w-md mx-auto">
            🙏 आपकी वर्तमान रिपोर्ट पूर्ण है। ये केवल अतिरिक्त शास्त्रीय गहराई हैं।
          </p>
        </div>
      )}
      {/* Ask another */}
      <div className="text-center pt-4">
        <Link to="/poocho-guruji" className="text-gold text-sm hover:underline" data-testid="ans-ask-another">
          ← दूसरा प्रश्न पूछें
        </Link>
      </div>
    </div>
  </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Feb 22, 2026 — ₹999 Premium Pack Active Banner
// Shows locked Kundli chip + days remaining + questions remaining, plus
// today's-quota indicator. Rendered above the birth-form only when the
// user's phone has an active premium pack.
// ═══════════════════════════════════════════════════════════════════════
const PremiumPackBanner = ({ pack }) => {
  const kundli = pack?.locked_kundli || {};
  const questionsUsed = pack?.questions_used || 0;
  const questionsRemaining = pack?.questions_remaining ?? 30;
  // Days remaining (activation date is Day 1; expires_at is 30 days later)
  let daysRemaining = null;
  try {
    if (pack?.expires_at) {
      const exp = new Date(pack.expires_at).getTime();
      const now = Date.now();
      daysRemaining = Math.max(0, Math.ceil((exp - now) / (24 * 60 * 60 * 1000)));
    }
  } catch (e) { /* noop */ }

  // Format place — DB stores lowercase-normalized; capitalize each word for display
  const placeDisplay = (kundli.birth_place || '')
    .split(/\s+/).map(w => w ? w[0].toUpperCase() + w.slice(1) : '').join(' ');

  return (
    <div
      className="mb-5 p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-amber-950/40 via-purple-950/30 to-amber-950/30 border border-gold/40 shadow-lg shadow-gold/10"
      data-testid="premium-pack-banner"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">🔒</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-gold font-bold">
              ₹999 पैक Active — Locked Kundli
            </span>
          </div>
          <div className="text-text-main text-sm font-heading leading-snug" data-testid="pack-locked-kundli">
            {kundli.birth_date || '—'} · {kundli.birth_time || '—'} · <span className="text-gold">{placeDisplay || '—'}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-text-muted mb-0.5">शेष दिन</div>
          <div className="text-xl font-bold text-gold leading-none" data-testid="pack-days-remaining">{daysRemaining ?? '—'}</div>
        </div>
      </div>

      {/* Questions progress bar */}
      <div className="mb-2 flex items-center justify-between text-[11px] text-text-muted">
        <span data-testid="pack-questions-used">प्रयोग: <span className="text-text-main font-semibold">{questionsUsed}/30</span></span>
        <span data-testid="pack-questions-remaining">शेष: <span className="text-gold font-semibold">{questionsRemaining} प्रश्न</span></span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-gold via-amber-400 to-amber-500 transition-all"
          style={{ width: `${Math.min(100, (questionsUsed / 30) * 100)}%` }}
        />
      </div>
      <p className="text-[10px] text-text-muted/80 mt-2.5 leading-relaxed">
        🌙 प्रति दिन 1 नया प्रश्न स्वीकार्य। पूर्व में पूछा गया प्रश्न quota consume नहीं करता। जन्म विवरण pack के दौरान बदला नहीं जा सकता।
      </p>
    </div>
  );
};

export { PremiumPackBanner };

